import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { TrendUp, Vault, Bank, Calculator, ShieldCheck } from '@phosphor-icons/react';

const DIMENSION_ICONS = {
  savings_rate: TrendUp,
  debt_levels: Bank,
  emergency_fund: ShieldCheck,
  retirement_preparedness: Vault,
  tax_efficiency: Calculator,
};

const DIMENSION_LABELS = {
  savings_rate: 'Savings Rate',
  debt_levels: 'Debt Levels',
  emergency_fund: 'Emergency Fund',
  retirement_preparedness: 'Retirement',
  tax_efficiency: 'Tax Efficiency',
};

const SharedScorePage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${BACKEND_URL}/api/shared-score/${token}`)
      .then(response => {
        setData(response.data);
        setLoading(false);
      })
      .catch(err => {
        setError('Score not found or expired');
        setLoading(false);
      });
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FDFCFB] text-[#4A5568]">Loading...</div>;
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCFB]">
        <div className="text-center">
          <h1 className="cabinet-grotesk text-2xl text-[#0A1128] mb-2">Score Not Found</h1>
          <p className="text-[#4A5568] mb-4">{error || 'This shared score is no longer available.'}</p>
          <Link to="/" className="text-[#C5A059] hover:underline">Go to PLANIVANCE AI</Link>
        </div>
      </div>
    );
  }

  const radarData = Object.entries(data.breakdown).map(([key, val]) => ({
    dimension: DIMENSION_LABELS[key],
    score: val.score,
    fullMark: val.max
  }));

  const gradeColor = data.total_score >= 80 ? '#2D6A4F' : data.total_score >= 60 ? '#C5A059' : data.total_score >= 40 ? '#B7791F' : '#C53030';

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      {/* Header */}
      <header className="bg-[#0A1128] text-white py-6 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="cabinet-grotesk text-xl font-medium text-[#C5A059]">PLANIVANCE AI</h1>
            <p className="text-xs text-[#A0AABF]">Wealth Health Score™</p>
          </div>
          <Link to="/" className="text-sm text-[#C5A059] hover:underline">Get Yours →</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 md:p-12 space-y-8">
        <div className="text-center">
          <p className="text-sm text-[#4A5568] mb-2">Shared by</p>
          <h1 className="cabinet-grotesk text-3xl text-[#0A1128] mb-1">{data.full_name}</h1>
          <p className="text-sm text-[#4A5568]">Shared on {new Date(data.shared_at).toLocaleDateString()}</p>
        </div>

        {/* Score Display */}
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-4">Wealth Health Score™</p>
          <div className="relative inline-block">
            <svg className="transform -rotate-90 w-48 h-48">
              <circle cx="96" cy="96" r="84" stroke="#E2E8F0" strokeWidth="12" fill="none" />
              <circle cx="96" cy="96" r="84" stroke={gradeColor} strokeWidth="12" fill="none"
                strokeDasharray={`${2 * Math.PI * 84}`}
                strokeDashoffset={`${2 * Math.PI * 84 * (1 - data.total_score / 100)}`}
                strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-6xl font-semibold text-[#0A1128] ibm-plex">{data.total_score}</span>
              <span className="text-xl font-medium" style={{ color: gradeColor }}>{data.letter_grade}</span>
            </div>
          </div>
          <p className="text-sm text-[#4A5568] mt-4">out of 100</p>
        </div>

        {/* Radar Chart */}
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6">
          <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Dimension Analysis</h2>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="dimension" style={{ fontSize: '11px', fill: '#4A5568' }} />
              <PolarRadiusAxis domain={[0, 20]} style={{ fontSize: '10px' }} />
              <Radar name="Score" dataKey="score" stroke="#C5A059" fill="#C5A059" fillOpacity={0.4} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Dimensions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(data.breakdown).map(([key, val]) => {
            const Icon = DIMENSION_ICONS[key];
            const dimColor = val.score >= 16 ? '#2D6A4F' : val.score >= 10 ? '#C5A059' : val.score >= 5 ? '#B7791F' : '#C53030';
            return (
              <div key={key} className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 rounded-md" style={{ backgroundColor: `${dimColor}15` }}>
                    <Icon size={22} weight="duotone" style={{ color: dimColor }} />
                  </div>
                  <h3 className="font-medium text-[#0A1128] text-sm">{DIMENSION_LABELS[key]}</h3>
                </div>
                <div className="flex items-end justify-between mb-2">
                  <span className="text-2xl font-semibold ibm-plex" style={{ color: dimColor }}>{val.score}</span>
                  <span className="text-sm text-[#4A5568]">/ {val.max}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="bg-[#0A1128] text-white rounded-md p-8 text-center">
          <h3 className="cabinet-grotesk text-2xl text-[#C5A059] mb-2">Get Your Own Score</h3>
          <p className="text-sm text-[#A0AABF] mb-4">Free educational financial planning tools</p>
          <Link to="/" className="inline-block bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A1128] px-6 py-2.5 rounded-md font-medium transition-colors">
            Try PLANIVANCE AI
          </Link>
        </div>

        {/* Disclaimer */}
        <div className="text-xs text-[#4A5568] leading-relaxed border-t border-slate-200 pt-6">
          <p><strong>Educational Use Only:</strong> The Wealth Health Score™ is an educational composite based on self-reported data. It does not constitute financial, investment, tax, or legal advice.</p>
          <p className="mt-3 text-[#0A1128] font-medium">PLANIVANCE AI — A Product of Outsource Solution Strategies, LLC</p>
        </div>
      </main>
    </div>
  );
};

export default SharedScorePage;
